import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  DaysPicker,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHead
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import {
  type Coupon,
  type CouponKind,
  type LoyaltyLevel,
  type LoyaltyPromo,
  ALL_DAYS_MASK,
  describeDaysMask,
  describeTimeWindow
} from '@/lib/config'
import { MAX_USAGE_LIMIT } from '@billiard/shared'
import { money, dateOnly, msToDateInput, dateInputToMs } from '@/lib/format'
import { Cur } from '@/lib/currency'

type Tab = 'levels' | 'promos' | 'coupons'

/** «12 / 100» для лимита, «12 / ∞» — если ограничения нет. */
function usageLabel(limit: number | null | undefined, used: number | undefined): string {
  return `${used ?? 0} / ${limit == null ? '∞' : limit}`
}

export function Loyalty(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>('levels')
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Лояльность"
        lead="Уровни клиентов (порог, скидка, начисление бонусов), временные промо-акции и купоны (скидка % или бесплатное время). Купоны можно выдавать и здесь, и на терминале — они синхронизируются, погашение фиксируется на терминале."
      />
      <ClubGate>
        <div className="toolbar">
          <div className="tabs">
            <button
              className={`tab${tab === 'levels' ? ' is-active' : ''}`}
              onClick={() => setTab('levels')}
            >
              Уровни
            </button>
            <button
              className={`tab${tab === 'promos' ? ' is-active' : ''}`}
              onClick={() => setTab('promos')}
            >
              Промо
            </button>
            <button
              className={`tab${tab === 'coupons' ? ' is-active' : ''}`}
              onClick={() => setTab('coupons')}
            >
              Купоны
            </button>
          </div>
        </div>
        {tab === 'levels' ? <LevelsTable /> : tab === 'promos' ? <PromosTable /> : <CouponsTable />}
      </ClubGate>
    </>
  )
}

// ═══════════════════════════════ Уровни ═══════════════════════════════

function LevelsTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<LoyaltyLevel[]>('/v1/loyalty/levels', query, [query?.clubId])
  const [editing, setEditing] = useState<LoyaltyLevel | 'new' | null>(null)
  const [deleting, setDeleting] = useState<LoyaltyLevel | null>(null)
  const del = useMutate()

  const rows = [...(list.data ?? [])].sort((a, b) => a.threshold - b.threshold)

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/loyalty/levels/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} уровень(-ей)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый уровень
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Уровни лояльности ещё не заданы."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">
                  Порог, <Cur />
                </th>
                <th className="num">Скидка</th>
                <th className="num">Бонусы</th>
                <th style={{ width: 60 }} title="Скидка распространяется на бар/кухню">
                  🍺
                </th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id}>
                  <td>
                    {l.color && <span className="swatch" style={{ background: l.color }} />}
                    {l.name}
                  </td>
                  <td className="num">
                    {money(l.threshold)} <Cur />
                  </td>
                  <td className="num">{l.discountPercent} %</td>
                  <td className="num">{l.bonusPercent} %</td>
                  <td className="num">{l.barDiscountEnabled ? '✓' : '—'}</td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(l)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(l)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <LevelForm
          level={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить уровень"
          message={`Удалить уровень «${deleting.name}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function LevelForm({
  level,
  query,
  onClose,
  onSaved
}: {
  level: LoyaltyLevel | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = level !== null
  const [name, setName] = useState(level?.name ?? '')
  const [threshold, setThreshold] = useState(level ? String(level.threshold) : '0')
  const [discount, setDiscount] = useState(level ? String(level.discountPercent) : '0')
  const [bonus, setBonus] = useState(level ? String(level.bonusPercent) : '0')
  const [color, setColor] = useState(level?.color ?? '#c9a84c')
  const [position, setPosition] = useState(level?.position != null ? String(level.position) : '')
  const [barDiscountEnabled, setBarDiscountEnabled] = useState(level?.barDiscountEnabled ?? false)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const pct = (v: string): number => Number(v)
  const validPct = (v: string): boolean => Number.isFinite(pct(v)) && pct(v) >= 0 && pct(v) <= 100

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название уровня.')
    const thr = Number(threshold)
    if (!Number.isFinite(thr) || thr < 0)
      return setValidation('Порог должен быть неотрицательным числом.')
    if (!validPct(discount) || !validPct(bonus))
      return setValidation('Скидка и бонусы — проценты от 0 до 100.')
    const posNum = position.trim() === '' ? null : Number(position)
    if (posNum !== null && (!Number.isInteger(posNum) || posNum < 0))
      return setValidation('Позиция должна быть неотрицательным целым числом.')
    setValidation(null)

    const body = {
      name: trimmed,
      threshold: thr,
      discountPercent: pct(discount),
      bonusPercent: pct(bonus),
      color: color || null,
      position: posNum,
      barDiscountEnabled
    }

    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/loyalty/levels/${level.id}`, body, { query })
        : api.post('/v1/loyalty/levels', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить уровень' : 'Новый уровень'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="level-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="level-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Название" htmlFor="l-name" full>
            <Input
              id="l-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Серебро"
              autoFocus
            />
          </Field>
          <Field
            label={
              <>
                Порог, <Cur />
              </>
            }
            htmlFor="l-thr"
          >
            <Input
              id="l-thr"
              type="number"
              min={0}
              step="any"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </Field>
          <Field label="Позиция" htmlFor="l-pos">
            <Input
              id="l-pos"
              type="number"
              min={0}
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="порядок"
            />
          </Field>
          <Field label="Скидка, %" htmlFor="l-disc">
            <Input
              id="l-disc"
              type="number"
              min={0}
              max={100}
              step="any"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
          </Field>
          <Field label="Бонусы, %" htmlFor="l-bonus">
            <Input
              id="l-bonus"
              type="number"
              min={0}
              max={100}
              step="any"
              value={bonus}
              onChange={(e) => setBonus(e.target.value)}
            />
          </Field>
          <Field label="Цвет" htmlFor="l-color" full>
            <Input
              id="l-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </Field>
          <div className="field field--full">
            <label className="checkbox">
              <input
                id="l-bar-disc"
                type="checkbox"
                checked={barDiscountEnabled}
                onChange={(e) => setBarDiscountEnabled(e.target.checked)}
              />
              Распространять скидку на бар/кухню
            </label>
          </div>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

// ═══════════════════════════════ Промо ═══════════════════════════════

function PromosTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<LoyaltyPromo[]>('/v1/loyalty/promos', query, [query?.clubId])
  const [editing, setEditing] = useState<LoyaltyPromo | 'new' | null>(null)
  const [deleting, setDeleting] = useState<LoyaltyPromo | null>(null)
  const del = useMutate()

  const rows = list.data ?? []

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/loyalty/promos/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  const periodLabel = (p: LoyaltyPromo): string => {
    if (p.activeFrom == null && p.activeTo == null) return 'Бессрочно'
    const from = p.activeFrom != null ? dateOnly(p.activeFrom) : '…'
    const to = p.activeTo != null ? dateOnly(p.activeTo) : '…'
    return `${from} — ${to}`
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} промо</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новое промо
          </Button>
        </div>
      </div>

      <AsyncState query={list} isEmpty={rows.length === 0} empty="Промо-акции ещё не заданы." />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">Скидка</th>
                <th className="num">Бонусы</th>
                <th style={{ width: 60 }} title="Скидка распространяется на бар/кухню">
                  🍺
                </th>
                <th>Время / дни</th>
                <th>Период</th>
                <th className="num" title="Применений: использовано из лимита">
                  Применений
                </th>
                <th>Статус</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td className="num">{p.discountPercent} %</td>
                  <td className="num">{p.bonusPercent} %</td>
                  <td className="num">{p.barDiscountEnabled ? '✓' : '—'}</td>
                  <td>
                    {describeTimeWindow(p.timeFrom, p.timeTo)} · {describeDaysMask(p.daysMask)}
                  </td>
                  <td>{periodLabel(p)}</td>
                  <td className="num">{usageLabel(p.usageLimit, p.usedCount)}</td>
                  <td>
                    <span className={`tag ${p.isActive === false ? 'tag--off' : 'tag--on'}`}>
                      {p.isActive === false ? 'Выкл' : 'Вкл'}
                    </span>
                  </td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(p)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(p)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PromoForm
          promo={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить промо"
          message={`Удалить промо «${deleting.name}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function PromoForm({
  promo,
  query,
  onClose,
  onSaved
}: {
  promo: LoyaltyPromo | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = promo !== null
  const [name, setName] = useState(promo?.name ?? '')
  const [discount, setDiscount] = useState(promo ? String(promo.discountPercent) : '0')
  const [bonus, setBonus] = useState(promo ? String(promo.bonusPercent) : '0')
  const [scheduled, setScheduled] = useState(
    Boolean(promo?.timeFrom || promo?.timeTo || promo?.daysMask)
  )
  const [timeFrom, setTimeFrom] = useState(promo?.timeFrom ?? '')
  const [timeTo, setTimeTo] = useState(promo?.timeTo ?? '')
  const [daysMask, setDaysMask] = useState(promo?.daysMask ?? ALL_DAYS_MASK)
  const [activeFrom, setActiveFrom] = useState(
    promo?.activeFrom != null ? msToDateInput(promo.activeFrom) : ''
  )
  const [activeTo, setActiveTo] = useState(
    promo?.activeTo != null ? msToDateInput(promo.activeTo) : ''
  )
  const [isActive, setIsActive] = useState(promo?.isActive !== false)
  const [barDiscountEnabled, setBarDiscountEnabled] = useState(promo?.barDiscountEnabled ?? false)
  // Лимит применений: чекбокс «без ограничения» + число 1…MAX_USAGE_LIMIT.
  const [limited, setLimited] = useState(promo?.usageLimit != null)
  const [usageLimit, setUsageLimit] = useState(
    promo?.usageLimit != null ? String(promo.usageLimit) : '100'
  )
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const validPct = (v: string): boolean => {
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 && n <= 100
  }

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название промо.')
    if (!validPct(discount) || !validPct(bonus))
      return setValidation('Скидка и бонусы — проценты от 0 до 100.')
    if (activeFrom && activeTo && dateInputToMs(activeFrom) > dateInputToMs(activeTo))
      return setValidation('Начало периода позже окончания.')
    const limit = limited ? Math.round(Number(usageLimit)) : null
    if (limit != null && (!Number.isFinite(limit) || limit < 1 || limit > MAX_USAGE_LIMIT))
      return setValidation(`Количество применений — целое от 1 до ${MAX_USAGE_LIMIT}.`)
    setValidation(null)

    const body = {
      name: trimmed,
      discountPercent: Number(discount),
      bonusPercent: Number(bonus),
      timeFrom: scheduled ? timeFrom || null : null,
      timeTo: scheduled ? timeTo || null : null,
      daysMask: scheduled ? daysMask : null,
      activeFrom: activeFrom ? dateInputToMs(activeFrom) : null,
      activeTo: activeTo ? dateInputToMs(activeTo) : null,
      isActive,
      barDiscountEnabled,
      usageLimit: limit
    }

    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/loyalty/promos/${promo.id}`, body, { query })
        : api.post('/v1/loyalty/promos', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить промо' : 'Новое промо'}
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="promo-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="promo-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Название" htmlFor="p-name" full>
            <Input
              id="p-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Счастливые часы"
              autoFocus
            />
          </Field>
          <Field label="Скидка, %" htmlFor="p-disc">
            <Input
              id="p-disc"
              type="number"
              min={0}
              max={100}
              step="any"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
          </Field>
          <Field label="Бонусы, %" htmlFor="p-bonus">
            <Input
              id="p-bonus"
              type="number"
              min={0}
              max={100}
              step="any"
              value={bonus}
              onChange={(e) => setBonus(e.target.value)}
            />
          </Field>

          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={limited}
                onChange={(e) => setLimited(e.target.checked)}
              />
              Ограничить количество применений
            </label>
          </div>
          {limited && (
            <Field
              label="Применений всего"
              htmlFor="p-uses"
              hint={
                promo?.usedCount
                  ? `Уже применено: ${promo.usedCount}. Счётчик ведёт терминал.`
                  : `От 1 до ${MAX_USAGE_LIMIT}. Когда лимит исчерпан, акция перестаёт предлагаться в окне оплаты.`
              }
            >
              <Input
                id="p-uses"
                type="number"
                min={1}
                max={MAX_USAGE_LIMIT}
                step={1}
                value={usageLimit}
                onChange={(e) => setUsageLimit(e.target.value)}
              />
            </Field>
          )}

          <Field label="Активно с" htmlFor="p-af">
            <Input
              id="p-af"
              type="date"
              value={activeFrom}
              onChange={(e) => setActiveFrom(e.target.value)}
            />
          </Field>
          <Field label="Активно по" htmlFor="p-at">
            <Input
              id="p-at"
              type="date"
              value={activeTo}
              onChange={(e) => setActiveTo(e.target.value)}
            />
          </Field>

          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={scheduled}
                onChange={(e) => setScheduled(e.target.checked)}
              />
              Ограничить по времени суток / дням недели
            </label>
          </div>

          {scheduled && (
            <>
              <Field label="С (ЧЧ:ММ)" htmlFor="p-from">
                <Input
                  id="p-from"
                  type="time"
                  value={timeFrom}
                  onChange={(e) => setTimeFrom(e.target.value)}
                />
              </Field>
              <Field label="По (ЧЧ:ММ)" htmlFor="p-to">
                <Input
                  id="p-to"
                  type="time"
                  value={timeTo}
                  onChange={(e) => setTimeTo(e.target.value)}
                />
              </Field>
              <Field label="Дни недели" full>
                <DaysPicker mask={daysMask} onChange={setDaysMask} />
              </Field>
            </>
          )}

          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              Промо включено
            </label>
          </div>
          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={barDiscountEnabled}
                onChange={(e) => setBarDiscountEnabled(e.target.checked)}
              />
              Распространять скидку на бар/кухню
            </label>
          </div>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}

// ═══════════════════════════════ Купоны ═══════════════════════════════

const FREE_MINUTES_PRESETS = [30, 60, 90, 120, 180]

/** Купон исчерпан: применений столько же, сколько разрешено лимитом. */
function couponUsedUp(c: Coupon): boolean {
  return (c.usedCount ?? 0) >= (c.usageLimit ?? 1)
}

function couponStatus(c: Coupon): { label: string; cls: string } {
  if (couponUsedUp(c)) return { label: 'Использован', cls: 'muted' }
  if (!c.isActive) return { label: 'Деактивирован', cls: 'muted' }
  if (c.expiresAt != null && Date.now() > c.expiresAt) return { label: 'Истёк', cls: 'muted' }
  return { label: 'Активен', cls: 'badge' }
}

function couponValue(c: Coupon): string {
  return c.kind === 'percent' ? `Скидка ${c.discountPercent}%` : `${c.freeMinutes} мин бесплатно`
}

function CouponsTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<Coupon[]>('/v1/coupons', query, [query?.clubId])
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<Coupon | null>(null)
  const del = useMutate()
  const toggle = useMutate()

  const rows = list.data ?? []

  const onToggle = async (c: Coupon): Promise<void> => {
    const ok = await toggle.mutate(() =>
      api.patch(`/v1/coupons/${c.id}`, { isActive: !c.isActive }, { query })
    )
    if (ok) list.reload()
  }

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/coupons/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} купон(ов)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setCreating(true)}>
            {Icon.plus}
            Новый купон
          </Button>
        </div>
      </div>

      <AsyncState query={list} isEmpty={rows.length === 0} empty="Купоны ещё не выданы." />
      {toggle.error && (
        <div className="form-error" style={{ marginTop: 12 }}>
          {toggle.error}
        </div>
      )}

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Код</th>
                <th>Значение</th>
                <th className="num" title="Применений: использовано из лимита">
                  Применений
                </th>
                <th>Статус</th>
                <th>Срок</th>
                <th style={{ width: 150 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const st = couponStatus(c)
                return (
                  <tr key={c.id}>
                    <td className="mono">{c.code}</td>
                    <td>
                      {couponValue(c)}
                      {c.note ? ` · ${c.note}` : ''}
                    </td>
                    <td className="num">{usageLabel(c.usageLimit ?? 1, c.usedCount)}</td>
                    <td>
                      <span className={st.cls}>{st.label}</span>
                    </td>
                    <td className="muted">
                      {c.expiresAt != null ? dateOnly(c.expiresAt) : 'бессрочно'}
                    </td>
                    <td>
                      <div className="row-actions">
                        {!couponUsedUp(c) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={toggle.saving}
                            onClick={() => void onToggle(c)}
                          >
                            {c.isActive ? 'Выключить' : 'Включить'}
                          </Button>
                        )}
                        {(c.usedCount ?? 0) === 0 && (
                          <IconButton danger title="Удалить" onClick={() => setDeleting(c)}>
                            {Icon.trash}
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <CouponForm
          query={query}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить купон"
          message={`Удалить купон ${deleting.code}? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function CouponForm({
  query,
  onClose,
  onSaved
}: {
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const [kind, setKind] = useState<CouponKind>('free_time')
  const [discountPercent, setDiscountPercent] = useState('10')
  const [freeMinutes, setFreeMinutes] = useState('60')
  const [barDiscountEnabled, setBarDiscountEnabled] = useState(false)
  const [note, setNote] = useState('')
  // Сколько раз купон можно применить; 1 — одноразовый (поведение по умолчанию).
  const [usageLimit, setUsageLimit] = useState('1')
  // Пустая дата = бессрочный купон.
  const [expires, setExpires] = useState('')
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    if (kind === 'percent') {
      const pct = Number(discountPercent)
      if (!Number.isFinite(pct) || pct < 1 || pct > 100)
        return setValidation('Скидка купона — от 1 до 100%.')
    } else {
      const mins = Number(freeMinutes)
      if (!Number.isInteger(mins) || mins <= 0)
        return setValidation('Время купона — целое число минут больше нуля.')
    }
    const uses = Math.round(Number(usageLimit))
    if (!Number.isFinite(uses) || uses < 1 || uses > MAX_USAGE_LIMIT)
      return setValidation(`Количество применений — целое от 1 до ${MAX_USAGE_LIMIT}.`)
    setValidation(null)

    const body = {
      kind,
      discountPercent: kind === 'percent' ? Number(discountPercent) : undefined,
      freeMinutes: kind === 'free_time' ? Number(freeMinutes) : undefined,
      note: note.trim() || null,
      expiresAt: expires ? dateInputToMs(expires) : null,
      barDiscountEnabled: kind === 'percent' ? barDiscountEnabled : undefined,
      usageLimit: uses
    }
    const ok = await save.mutate(() => api.post('/v1/coupons', body, { query }))
    if (ok) onSaved()
  }

  return (
    <Modal
      title="Новый купон"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="coupon-form">
            Выдать купон
          </Button>
        </>
      }
    >
      <form id="coupon-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Тип купона" full>
            <div className="tabs">
              <button
                type="button"
                className={`tab${kind === 'free_time' ? ' is-active' : ''}`}
                onClick={() => setKind('free_time')}
              >
                Бесплатное время
              </button>
              <button
                type="button"
                className={`tab${kind === 'percent' ? ' is-active' : ''}`}
                onClick={() => setKind('percent')}
              >
                Скидка %
              </button>
            </div>
          </Field>

          {kind === 'free_time' ? (
            <Field label="Бесплатное время, мин" htmlFor="cp-mins" full>
              <div className="days" style={{ marginBottom: 8 }}>
                {FREE_MINUTES_PRESETS.map((m) => (
                  <button
                    key={m}
                    type="button"
                    className={`day-chip${Number(freeMinutes) === m ? ' is-on' : ''}`}
                    aria-pressed={Number(freeMinutes) === m}
                    onClick={() => setFreeMinutes(String(m))}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <Input
                id="cp-mins"
                type="number"
                min={1}
                value={freeMinutes}
                onChange={(e) => setFreeMinutes(e.target.value)}
              />
            </Field>
          ) : (
            <Field label="Скидка, %" htmlFor="cp-pct" full>
              <Input
                id="cp-pct"
                type="number"
                min={1}
                max={100}
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
              />
              <label className="checkbox" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={barDiscountEnabled}
                  onChange={(e) => setBarDiscountEnabled(e.target.checked)}
                />
                Распространять скидку на бар/кухню
              </label>
            </Field>
          )}

          <Field
            label="Количество применений"
            htmlFor="cp-uses"
            hint={`1 — одноразовый купон. До ${MAX_USAGE_LIMIT}: купон принимается, пока не исчерпан.`}
          >
            <Input
              id="cp-uses"
              type="number"
              min={1}
              max={MAX_USAGE_LIMIT}
              step={1}
              value={usageLimit}
              onChange={(e) => setUsageLimit(e.target.value)}
            />
          </Field>
          <Field label="Действует до" htmlFor="cp-exp">
            <Input
              id="cp-exp"
              type="date"
              value={expires}
              onChange={(e) => setExpires(e.target.value)}
            />
            <span className="field__hint">Пусто — бессрочный.</span>
          </Field>
          <Field label="Заметка" htmlFor="cp-note" full>
            <Input
              id="cp-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="напр. для VIP-клиента"
            />
          </Field>
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}
